import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  CheckCircle,
  QrCode2,
  Person,
  PhoneIphoneOutlined,
  HelpOutline,
  Link as LinkIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { gradients, transparentColors } from "../../../utils/theme";

/**
 * Hero Section Component - MallInHand
 *
 * Displays the main hero section with heading, subtitle, and animated before/after comparison
 * showing traditional parking queues vs MallInHand's instant QR entry system.
 * Features premium navy gradient theme with luxury aesthetics.
 */
const HeroSection: React.FC = () => {
  return (
    <Box
      id="hero"
      sx={{
        background: gradients.navyLux,
        color: "white",
        pt: { xs: 9, md: 10 },
        pb: { xs: 6, md: 8 },
        overflow: "hidden",
        position: "relative",
        willChange: "transform",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
        WebkitFontSmoothing: "antialiased",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage:
            "radial-gradient(circle at 20% 50%, rgba(212, 175, 55, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 215, 0, 0.1) 0%, transparent 50%)",
          pointerEvents: "none",
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative" }}>
        <motion.div
          initial={{ opacity: 0, y: -30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: {
                xs: "1.75rem",
                sm: "2.5rem",
                md: "3.5rem",
                lg: "4.5rem",
              },
              fontWeight: 700,
              mb: 2,
              letterSpacing: { xs: "-0.5px", sm: "-1px", md: "-1.5px" },
              textAlign: "center",
              textShadow: "0 2px 30px rgba(0, 0, 0, 0.1)",
            }}
          >
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <Box component="span" sx={{ color: "white" }}>
                Explore Your Favorite Malls -{" "}
              </Box>
              <Box
                component="span"
                sx={{
                  background: gradients.gold,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Digitally
              </Box>
            </motion.span>
          </Typography>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <Typography
              variant="h5"
              sx={{
                fontSize: {
                  xs: "0.95rem",
                  sm: "1.125rem",
                  md: "1.35rem",
                  lg: "1.5rem",
                },
                fontWeight: 400,
                mb: 1,
                opacity: 0.95,
                maxWidth: "900px",
                mx: "auto",
                textAlign: "center",
                textShadow: "0 1px 10px rgba(0,0,0,0.1)",
              }}
            >
              Discover brands, stores, offers, events and buy parking instantly.
            </Typography>
          </motion.div>
        </motion.div>

        {/* Before/After Comparison Animation */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* BEFORE - Traditional Queue */}
          <Grid item xs={12} sm={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              whileHover={{ scale: 1.02 }}
            >
              <Card
                sx={{
                  bgcolor: transparentColors.white20,
                  backdropFilter: "blur(10px)",
                  border: "2px solid rgba(255,255,255,0.5)",
                  height: "100%",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography
                      variant="h5"
                      fontWeight={600}
                      color="white"
                      sx={{
                        fontSize: {
                          xs: "1.25rem",
                          sm: "1.5rem",
                          md: "1.75rem",
                        },
                      }}
                    >
                      Traditional Entry
                    </Typography>
                  </Box>

                  {/* Animated Queue - Slow Sequential Processing */}
                  <Box
                    sx={{
                      position: "relative",
                      height: { xs: 120, sm: 160, md: 200 },
                      display: "flex",
                      alignItems: "center",
                      overflow: "visible",
                      width: "100%",
                    }}
                  >
                    {/* Security Guard Person - Showing QR and Passing Tickets */}
                    <Box
                      sx={{
                        position: "absolute",
                        right: { xs: 20, sm: 40, md: 60 },
                        top: "50%",
                        transform: "translateY(-50%)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        bgcolor: "rgba(255,255,255,0.25)",
                        borderRadius: 2,
                        p: { xs: 1, sm: 1.25, md: 1.5 },
                        zIndex: 10,
                      }}
                    >
                      <Person
                        sx={{
                          fontSize: { xs: 28, sm: 34, md: 40 },
                          color: "white",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: "white",
                          fontSize: {
                            xs: "0.6rem",
                            sm: "0.625rem",
                            md: "0.65rem",
                          },
                        }}
                      >
                        Security
                      </Typography>

                      {/* QR Code near guard - appears when customer arrives, 4 instances for 4 customers */}
                      {[0, 1, 2, 3].map((qrIndex) => (
                        <motion.div
                          key={`guard-qr-${qrIndex}`}
                          animate={{
                            opacity: [0, 0, 0, 0, 0, 0, 1, 1, 1, 0, 0],
                            scale: [0, 0, 0, 0, 0, 0, 1, 1.1, 1.1, 0.8, 0],
                          }}
                          transition={{
                            duration: 20,
                            repeat: Infinity,
                            delay: qrIndex * 5 - 10,
                            times: [
                              0, 0.2, 0.35, 0.5, 0.51, 0.53, 0.56, 0.6, 0.64,
                              0.65, 0.66,
                            ],
                          }}
                          style={{
                            position: "absolute",
                            left: -35,
                            top: 5,
                            zIndex: 15,
                          }}
                        >
                          <Box
                            sx={{
                              bgcolor: "white",
                              borderRadius: 0.5,
                              p: 0.5,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
                              border: "2px solid",
                              borderColor: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <QrCode2
                              sx={{
                                fontSize: 20,
                                color: "#1a237e",
                              }}
                            />
                          </Box>
                        </motion.div>
                      ))}

                      {/* Scanning beam from customer phone to QR - 4 instances - Hidden on mobile */}
                      <Box sx={{ display: { xs: "none", sm: "block" } }}>
                        {[0, 1, 2, 3].map((beamIndex) => (
                          <motion.div
                            key={`scan-beam-${beamIndex}`}
                            animate={{
                              scaleX: [0, 0, 0, 0, 0, 0, 0, 1, 1, 0, 0],
                              opacity: [0, 0, 0, 0, 0, 0, 0, 1, 1, 0, 0],
                            }}
                            transition={{
                              duration: 20,
                              repeat: Infinity,
                              delay: beamIndex * 5 - 10,
                              times: [
                                0, 0.2, 0.35, 0.5, 0.51, 0.53, 0.56, 0.6, 0.64,
                                0.65, 0.66,
                              ],
                              ease: "easeInOut",
                            }}
                            style={{
                              position: "absolute",
                              left: -140,
                              top: "50%",
                              transformOrigin: "left center",
                              zIndex: 4,
                            }}
                          >
                            <Box
                              sx={{
                                width: 105,
                                height: 4,
                                background:
                                  "linear-gradient(90deg, rgba(26, 35, 126, 1) 0%, rgba(26, 35, 126, 0.9) 80%, rgba(255, 152, 0, 0) 100%)",
                                boxShadow:
                                  "0 0 15px rgba(26, 35, 126, 0.8), 0 2px 8px rgba(26, 35, 126, 0.5)",
                                borderRadius: 2,
                              }}
                            />
                          </motion.div>
                        ))}
                      </Box>

                      {/* Ticket appears after scan and moves to customer - 4 instances */}
                      <Box sx={{ display: { xs: "none", sm: "block" } }}>
                        {[0, 1, 2, 3].map((ticketIndex) => (
                          <motion.div
                            key={`ticket-pass-${ticketIndex}`}
                            animate={{
                              x: [
                                -1000, -1000, -1000, -1000, -1000, -1000, -1000,
                                -1000, -1000, 0, -60, -120, -120, -1000,
                              ],
                              y: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
                              opacity: [
                                0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 0, 0,
                              ],
                              scale: [
                                0, 0, 0, 0, 0, 0, 0, 0, 0, 1.3, 1.1, 1, 0.5, 0,
                              ],
                            }}
                            transition={{
                              duration: 20,
                              repeat: Infinity,
                              delay: ticketIndex * 5 - 10,
                              times: [
                                0, 0.2, 0.35, 0.5, 0.51, 0.53, 0.56, 0.6, 0.64,
                                0.65, 0.66, 0.67, 0.68, 1,
                              ],
                              ease: "linear",
                            }}
                            style={{
                              position: "absolute",
                              left: -35,
                              top: 5,
                              zIndex: 30,
                            }}
                          >
                            {/* Realistic Parking Ticket */}
                            <Box
                              sx={{
                                width: { xs: 22, sm: 26, md: 28 },
                                height: { xs: 32, sm: 36, md: 40 },
                                bgcolor: "#FFF9E6",
                                borderRadius: 0.5,
                                border: "1.5px solid #FFD700",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.2,
                                py: 0.3,
                                px: 0.2,
                                position: "relative",
                                background:
                                  "linear-gradient(135deg, #FFFACD 0%, #FFF9E6 100%)",
                              }}
                            >
                              {/* P Symbol */}
                              <Typography
                                sx={{
                                  fontSize: { xs: 10, sm: 12, md: 14 },
                                  fontWeight: 900,
                                  color: "#1a237e",
                                  lineHeight: 1,
                                }}
                              >
                                P
                              </Typography>
                              {/* Ticket text */}
                              <Typography
                                sx={{
                                  fontSize: { xs: 5, sm: 6, md: 7 },
                                  fontWeight: 700,
                                  color: "#1a237e",
                                  lineHeight: 1,
                                  letterSpacing: 0.2,
                                }}
                              >
                                TICKET
                              </Typography>
                            </Box>
                          </motion.div>
                        ))}
                      </Box>
                    </Box>

                    {/* Queue of customers - SLOW sequential processing with detailed steps */}
                    {[0, 1, 2, 3].map((i) => {
                      const spacing = 60;
                      const queueStart = -100;
                      const queuePos1 = queueStart + spacing * 0; // First in queue
                      const queuePos2 = queueStart - spacing * 1; // Second
                      const queuePos3 = queueStart - spacing * 2; // Third
                      const queuePos4 = queueStart - spacing * 3; // Fourth
                      const approachGuard = 180; // Closer to guard for interaction
                      const guardPos = 200; // At guard
                      const exitPos = 600; // Exit through section

                      const positions = [
                        queuePos4,
                        queuePos3,
                        queuePos2,
                        queuePos1, // Waiting in queue
                        approachGuard, // Approach guard
                        approachGuard, // Wait at guard (no movement during interaction)
                        guardPos, // Pass guard
                        exitPos, // Exit
                      ];

                      return (
                        <motion.div
                          key={i}
                          animate={{
                            x: positions,
                          }}
                          transition={{
                            duration: 20,
                            repeat: Infinity,
                            delay: i * 5 - 10,
                            ease: "linear",
                            times: [0, 0.2, 0.35, 0.5, 0.52, 0.68, 0.7, 1], // Long pause at position 5-6 (guard interaction)
                          }}
                          style={{
                            position: "absolute",
                            left: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.5,
                              position: "relative",
                            }}
                          >
                            {/* Customer's phone - appears, scans, then disappears after ticket received */}
                            <motion.div
                              animate={{
                                opacity: [0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 0, 0],
                                x: [0, 0, 0, 0, 0, 0, 0, 25, 25, 25, 25, 0],
                                scale: [
                                  1, 1, 1, 1, 1, 1, 1, 1.1, 1.1, 1, 0.8, 0,
                                ],
                              }}
                              transition={{
                                duration: 20,
                                repeat: Infinity,
                                delay: i * 5 - 10,
                                times: [
                                  0, 0.2, 0.35, 0.5, 0.51, 0.53, 0.56, 0.6,
                                  0.64, 0.66, 0.68, 0.69,
                                ],
                              }}
                              style={{
                                position: "absolute",
                                top: -15,
                                left: -5,
                                zIndex: 20,
                              }}
                            >
                              <Box sx={{ position: "relative" }}>
                                <PhoneIphoneOutlined
                                  sx={{
                                    fontSize: 48,
                                    color: "white",
                                    filter:
                                      "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                                  }}
                                />
                                {/* Glow effect during scan */}
                                <motion.div
                                  transition={{
                                    duration: 20,
                                    repeat: Infinity,
                                    delay: i * 5 - 10,
                                    times: [
                                      0, 0.2, 0.35, 0.5, 0.51, 0.53, 0.56, 0.6,
                                      0.64, 0.66, 0.68, 0.69,
                                    ],
                                  }}
                                  style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    width: "60%",
                                    height: "70%",
                                    borderRadius: "8px",
                                    pointerEvents: "none",
                                  }}
                                />
                              </Box>
                            </motion.div>

                            {/* Customer icon */}
                            <Person
                              sx={{
                                fontSize: 40,
                                opacity: 0.9,
                                color: "white",
                              }}
                            />

                            {/* Ticket icon - appears in customer's hand after receiving */}
                            <motion.div
                              animate={{
                                opacity: [
                                  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1,
                                ],
                                scale: [
                                  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1.3, 1,
                                ],
                                rotate: [
                                  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 15, 0,
                                ],
                              }}
                              transition={{
                                duration: 20,
                                repeat: Infinity,
                                delay: i * 5 - 10,
                                times: [
                                  0, 0.2, 0.35, 0.5, 0.51, 0.53, 0.56, 0.6,
                                  0.64, 0.66, 0.68, 0.69, 1,
                                ],
                              }}
                              style={{
                                position: "absolute",
                                top: -15,
                                right: -12,
                                zIndex: 25,
                              }}
                            >
                              {/* Realistic Parking Ticket in Customer's Hand */}
                              <Box
                                sx={{
                                  width: { xs: 20, sm: 24, md: 26 },
                                  height: { xs: 28, sm: 32, md: 36 },
                                  bgcolor: "#FFF9E6",
                                  borderRadius: 0.5,
                                  border: "1.5px solid #FFD700",
                                  boxShadow: "0 3px 8px rgba(0,0,0,0.4)",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 0.2,
                                  py: 0.3,
                                  px: 0.2,
                                  position: "relative",
                                  background:
                                    "linear-gradient(135deg, #FFFACD 0%, #FFF9E6 100%)",
                                }}
                              >
                                {/* P Symbol */}
                                <Typography
                                  sx={{
                                    fontSize: { xs: 9, sm: 11, md: 13 },
                                    fontWeight: 900,
                                    color: "#1a237e",
                                    lineHeight: 1,
                                  }}
                                >
                                  P
                                </Typography>
                                {/* Ticket text */}
                                <Typography
                                  sx={{
                                    fontSize: { xs: 4.5, sm: 5.5, md: 6.5 },
                                    fontWeight: 700,
                                    color: "#1a237e",
                                    lineHeight: 1,
                                    letterSpacing: 0.2,
                                  }}
                                >
                                  TICKET
                                </Typography>
                              </Box>
                            </motion.div>

                            {/* Question mark - confusion about navigating mall */}
                            <motion.div
                              animate={{
                                opacity: [
                                  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1,
                                  1, 1, 1, 0,
                                ],
                                scale: [
                                  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1.1, 1,
                                  1.1, 1, 1.1, 0,
                                ],
                                y: [
                                  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, -3, 0,
                                  -3, 0, -3, 0,
                                ],
                              }}
                              transition={{
                                duration: 20,
                                repeat: Infinity,
                                delay: i * 5 - 10,
                                times: [
                                  0, 0.2, 0.35, 0.5, 0.51, 0.53, 0.56, 0.6,
                                  0.64, 0.66, 0.68, 0.69, 0.71, 0.75, 0.78,
                                  0.82, 0.85, 0.9, 0.95,
                                ],
                              }}
                              style={{
                                position: "absolute",
                                top: -50,
                                left: "50%",
                                transform: "translateX(-50%)",
                                zIndex: 30,
                              }}
                            >
                              <Box
                                sx={{
                                  bgcolor: "rgba(255, 152, 0, 0.95)",
                                  borderRadius: { xs: 2, sm: 2.5 },
                                  px: { xs: 1, sm: 1.5 },
                                  py: { xs: 0.5, sm: 0.8 },
                                  display: "flex",
                                  alignItems: "center",
                                  gap: { xs: 0.5, sm: 0.8 },
                                  boxShadow:
                                    "0 4px 12px rgba(255, 152, 0, 0.6)",
                                  border: "2px solid rgba(255, 255, 255, 0.8)",
                                  maxWidth: { xs: "120px", sm: "140px" },
                                }}
                              >
                                <HelpOutline
                                  sx={{
                                    fontSize: { xs: 18, sm: 22, md: 26 },
                                    color: "white",
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "white",
                                    fontWeight: 700,
                                    fontSize: {
                                      xs: "0.6rem",
                                      sm: "0.65rem",
                                      md: "0.7rem",
                                    },
                                    lineHeight: 1.2,
                                    textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                                  }}
                                >
                                  How to find anything?
                                </Typography>
                              </Box>
                            </motion.div>
                          </Box>
                        </motion.div>
                      );
                    })}
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Typography
                      variant="h3"
                      fontWeight={700}
                      sx={{
                        mb: 1,
                        color: "white",
                        fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
                      }}
                    >
                      30 sec + Confusion
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        opacity: 0.9,
                        color: "white",
                        fontSize: { xs: "0.875rem", sm: "0.95rem", md: "1rem" },
                      }}
                    >
                      Slow entry & no digital guidance of mall
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* AFTER - MallInHand Quick Entry - Continued in next message due to length... */}
          <Grid item xs={12} sm={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              whileHover={{ scale: 1.02 }}
            >
              <Card
                sx={{
                  bgcolor: transparentColors.white20,
                  backdropFilter: "blur(10px)",
                  border: "2px solid rgba(255,255,255,0.5)",
                  height: "100%",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography
                      variant="h5"
                      fontWeight={600}
                      color="white"
                      sx={{
                        fontSize: {
                          xs: "1.25rem",
                          sm: "1.5rem",
                          md: "1.75rem",
                        },
                      }}
                    >
                      Smart Entry
                    </Typography>
                  </Box>

                  {/* Animated Quick Entry - Fast Parallel Processing */}
                  <Box
                    sx={{
                      position: "relative",
                      height: { xs: 120, sm: 160, md: 200 },
                      display: "flex",
                      alignItems: "center",
                      overflow: "visible",
                      width: "100%",
                    }}
                  >
                    {/* Security Guard Person - Scanning QR Codes with Mobile */}
                    <Box
                      sx={{
                        position: "absolute",
                        right: { xs: 20, sm: 50, md: 80 },
                        top: "50%",
                        transform: "translateY(-50%)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        bgcolor: "rgba(255,255,255,0.25)",
                        borderRadius: 2,
                        p: { xs: 1, sm: 1.25, md: 1.5 },
                        zIndex: 5,
                      }}
                    >
                      <Box sx={{ position: "relative" }}>
                        <Person
                          sx={{
                            fontSize: { xs: 28, sm: 34, md: 40 },
                            color: "white",
                          }}
                        />
                        {/* Guard's mobile phone on left side - vertical, back camera pointing to QR */}
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: { xs: -3, sm: -4, md: -5 },
                            left: { xs: -16, sm: -19, md: -22 },
                            zIndex: 10,
                          }}
                        >
                          <Box sx={{ position: "relative" }}>
                            <PhoneIphoneOutlined
                              sx={{
                                fontSize: { xs: 24, sm: 30, md: 36 },
                                color: "white",
                                filter:
                                  "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                              }}
                            />
                            {/* Back camera indicator on top */}
                            <Box
                              sx={{
                                position: "absolute",
                                top: { xs: 2, sm: 2.5, md: 3 },
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: { xs: 6, sm: 7, md: 8 },
                                height: { xs: 6, sm: 7, md: 8 },
                                borderRadius: "50%",
                                bgcolor: "rgba(66, 165, 245, 0.9)",
                                border: "1.5px solid rgba(255,255,255,0.6)",
                                boxShadow: "0 0 12px rgba(66, 165, 245, 0.8)",
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: "white",
                          fontSize: {
                            xs: "0.6rem",
                            sm: "0.625rem",
                            md: "0.65rem",
                          },
                        }}
                      >
                        Security
                      </Typography>

                      {/* Checkmark icon - appears above guard's phone after scan with smooth animation */}
                      {[0, 1, 2, 3, 4, 5, 6, 7].map((checkIndex) => (
                        <motion.div
                          key={`guard-check-${checkIndex}`}
                          animate={{
                            opacity: [0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 0],
                            scale: [0, 0, 0, 0, 0, 0, 0, 1.2, 1, 1, 0.9],
                            rotate: [0, 0, 0, 0, 0, 0, 0, -10, 0, 0, 0],
                          }}
                          transition={{
                            duration: 10,
                            repeat: Infinity,
                            delay: checkIndex * 1.25 - 5,
                            times: [
                              0, 0.47, 0.54, 0.55, 0.56, 0.57, 0.575, 0.58, 0.6,
                              0.63, 0.66,
                            ],
                            ease: [0.4, 0, 0.2, 1],
                          }}
                          style={{
                            position: "absolute",
                            top: -8,
                            left: -28,
                            zIndex: 15,
                          }}
                        >
                          <Box
                            component={motion.div}
                            animate={{
                              boxShadow: [
                                "0 0 0px rgba(76, 175, 80, 0)",
                                "0 0 0px rgba(76, 175, 80, 0)",
                                "0 0 0px rgba(76, 175, 80, 0)",
                                "0 0 0px rgba(76, 175, 80, 0)",
                                "0 0 0px rgba(76, 175, 80, 0)",
                                "0 0 0px rgba(76, 175, 80, 0)",
                                "0 0 0px rgba(76, 175, 80, 0)",
                                "0 0 30px rgba(76, 175, 80, 0.9)",
                                "0 0 35px rgba(76, 175, 80, 1)",
                                "0 0 35px rgba(76, 175, 80, 1)",
                                "0 0 0px rgba(76, 175, 80, 0)",
                              ],
                            }}
                            transition={{
                              duration: 10,
                              repeat: Infinity,
                              delay: checkIndex * 1.25 - 5,
                              times: [
                                0, 0.47, 0.54, 0.55, 0.56, 0.57, 0.575, 0.58,
                                0.6, 0.63, 0.66,
                              ],
                              ease: [0.4, 0, 0.2, 1],
                            }}
                            sx={{
                              borderRadius: "50%",
                              display: "inline-flex",
                            }}
                          >
                            <CheckCircle
                              sx={{
                                fontSize: 32,
                                color: "success.main",
                                filter:
                                  "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                              }}
                            />
                          </Box>
                        </motion.div>
                      ))}
                    </Box>

                    {/* Queue of customers - FAST processing (MallInHand) */}
                    {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
                      const spacing = 60;
                      const queueStart = -100;
                      const queuePos1 = queueStart + spacing * 0;
                      const queuePos2 = queueStart - spacing * 1;
                      const queuePos3 = queueStart - spacing * 2;
                      const queuePos4 = queueStart - spacing * 3;
                      const approachGuardPos = 140;
                      const guardPos = 200;
                      const exitPos = 600;

                      const positions = [
                        queuePos4,
                        queuePos3,
                        queuePos2,
                        queuePos1,
                        approachGuardPos,
                        guardPos,
                        exitPos,
                      ];

                      return (
                        <motion.div
                          key={i}
                          animate={{
                            x: positions,
                          }}
                          transition={{
                            duration: 10,
                            repeat: Infinity,
                            delay: i * 1.25 - 5,
                            ease: "linear",
                            times: [0, 0.25, 0.35, 0.42, 0.48, 0.55, 1],
                          }}
                          style={{
                            position: "absolute",
                            left: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.5,
                              position: "relative",
                            }}
                          >
                            {/* Phone with QR code inside - static, no shake */}
                            <motion.div
                              animate={{
                                boxShadow: [
                                  "0 0 0px rgba(26, 35, 126, 0)",
                                  "0 0 0px rgba(26, 35, 126, 0)",
                                  "0 0 0px rgba(26, 35, 126, 0)",
                                  "0 0 0px rgba(26, 35, 126, 0)",
                                  "0 0 40px rgba(26, 35, 126, 0.9)",
                                  "0 0 50px rgba(26, 35, 126, 1)",
                                  "0 0 40px rgba(26, 35, 126, 0.9)",
                                  "0 0 0px rgba(102, 126, 234, 0)",
                                ],
                              }}
                              transition={{
                                duration: 10,
                                repeat: Infinity,
                                delay: i * 1.25 - 5,
                                times: [
                                  0, 0.47, 0.48, 0.5, 0.52, 0.54, 0.55, 0.6,
                                ],
                                ease: "easeInOut",
                              }}
                              style={{
                                position: "relative",
                                borderRadius: "12px",
                              }}
                            >
                              <PhoneIphoneOutlined
                                sx={{
                                  fontSize: { xs: 32, sm: 40, md: 48 },
                                  color: "white",
                                }}
                              />
                              {/* QR Code inside phone screen - always visible */}
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: "48%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  zIndex: 25,
                                }}
                              >
                                <Box
                                  sx={{
                                    bgcolor: "white",
                                    borderRadius: 0.3,
                                    p: 0.3,
                                    boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
                                    border: "1px solid",
                                    borderColor: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <QrCode2
                                    sx={{
                                      fontSize: { xs: 10, sm: 12, md: 14 },
                                      color: "#1a237e",
                                    }}
                                  />
                                </Box>
                              </Box>

                              {/* Digital Mall Link notification - appears after scan approved */}
                              <motion.div
                                animate={{
                                  opacity: [0, 0, 0, 0, 0, 1, 1, 1, 1, 0],
                                  scale: [0, 0, 0, 0, 0, 1, 1.1, 1, 1, 0],
                                  y: [0, 0, 0, 0, 0, 0, -3, 0, 0, 0],
                                }}
                                transition={{
                                  duration: 10,
                                  repeat: Infinity,
                                  delay: i * 1.25 - 5,
                                  times: [
                                    0, 0.35, 0.42, 0.47, 0.48, 0.52, 0.56, 0.65,
                                    0.8, 0.9,
                                  ],
                                  ease: [0.4, 0, 0.2, 1],
                                }}
                                style={{
                                  position: "absolute",
                                  top: "30%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  zIndex: 30,
                                }}
                              >
                                <Box
                                  component={motion.div}
                                  animate={{
                                    boxShadow: [
                                      "0 0 0px rgba(76, 175, 80, 0)",
                                      "0 0 0px rgba(76, 175, 80, 0)",
                                      "0 0 0px rgba(76, 175, 80, 0)",
                                      "0 0 0px rgba(76, 175, 80, 0)",
                                      "0 0 0px rgba(76, 175, 80, 0)",
                                      "0 4px 20px rgba(76, 175, 80, 0.8)",
                                      "0 6px 25px rgba(76, 175, 80, 1)",
                                      "0 4px 20px rgba(76, 175, 80, 0.8)",
                                      "0 4px 20px rgba(76, 175, 80, 0.8)",
                                      "0 0 0px rgba(76, 175, 80, 0)",
                                    ],
                                  }}
                                  transition={{
                                    duration: 10,
                                    repeat: Infinity,
                                    delay: i * 1.25 - 5,
                                    times: [
                                      0, 0.35, 0.42, 0.47, 0.48, 0.52, 0.56,
                                      0.65, 0.8, 0.9,
                                    ],
                                    ease: [0.4, 0, 0.2, 1],
                                  }}
                                  sx={{
                                    bgcolor: "rgba(76, 175, 80, 0.95)",
                                    borderRadius: { xs: 1.5, sm: 2 },
                                    px: { xs: 1, sm: 1.5 },
                                    py: { xs: 0.5, sm: 0.8 },
                                    display: "flex",
                                    alignItems: "center",
                                    gap: { xs: 0.4, sm: 0.6 },
                                    border:
                                      "2px solid rgba(255, 255, 255, 0.9)",
                                  }}
                                >
                                  <LinkIcon
                                    sx={{
                                      fontSize: { xs: 12, sm: 14, md: 16 },
                                      color: "white",
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "white",
                                      fontWeight: 700,
                                      fontSize: {
                                        xs: "0.6rem",
                                        sm: "0.65rem",
                                        md: "0.7rem",
                                      },
                                      whiteSpace: "nowrap",
                                      textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                                    }}
                                  >
                                    Digital Mall
                                  </Typography>
                                </Box>
                              </motion.div>
                            </motion.div>

                            {/* Customer icon */}
                            <Person
                              sx={{
                                fontSize: { xs: 28, sm: 34, md: 40 },
                                opacity: 0.9,
                                color: "white",
                              }}
                            />
                          </Box>
                        </motion.div>
                      );
                    })}
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Typography
                      variant="h3"
                      fontWeight={700}
                      sx={{
                        mb: 1,
                        color: "white",
                        fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
                      }}
                    >
                      1 sec + Exploration
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        opacity: 0.9,
                        color: "white",
                        fontSize: { xs: "0.875rem", sm: "0.95rem", md: "1rem" },
                      }}
                    >
                      Instant entry + digital mall access
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HeroSection;
